import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { AdminOnly } from '../components/common/RoleBasedComponent';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AdminPanelSettings as AdminIcon,
  Event as EventIcon,
  Group as TeamIcon,
  Person as ProfileIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  SportsEsports as GulagIcon,
  LocalBar as BarIcon
} from '@mui/icons-material';
import { resolveMediaUrl } from '../utils/mediaUrl';

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const { isAuthenticated, logout, user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleUserMenuNavigation = (path) => {
    navigate(path);
    handleUserMenuClose();
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const navigationItems = [
    ...(isAuthenticated ? [
      { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
      { label: 'Gulag', path: '/gulag', icon: <GulagIcon /> },
      { label: 'Events', path: '/events', icon: <EventIcon /> },
      { label: 'Bar', path: '/bar', icon: <BarIcon /> }
    ] : [])
  ];

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.main' }}>
          {settings.nav_title}
        </Typography>
        {isAuthenticated && user && (
          <Box 
            sx={{ 
              mt: 1, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              p: 1,
              borderRadius: 1,
            }}
          >
            <Avatar 
              sx={{ width: 24, height: 24, bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
              src={resolveMediaUrl(user.avatar)}
            >
              {user.username?.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" color="text.secondary">
              {user.username}
            </Typography>
            {user.role === 'admin' && (
              <Chip label="Admin" size="small" color="error" />
            )}
          </Box>
        )}
      </Box>
      
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={isActivePath(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  color: 'secondary.main',
                  borderLeft: '2px solid',
                  borderColor: 'secondary.main',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'secondary.main',
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        
        <AdminOnly>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavigation('/admin')}
              selected={isActivePath('/admin')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                  '&:hover': {
                    backgroundColor: 'error.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'error.contrastText',
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AdminIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Admin Panel" 
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>
        </AdminOnly>

        {isAuthenticated && (
          <>
            <ListItem disablePadding sx={{ mt: 2, borderTop: 1, borderColor: 'divider', pt: 1 }}>
              <ListItemButton onClick={() => handleNavigation('/profile')}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ProfileIcon />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigation('/teams')}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <TeamIcon />
                </ListItemIcon>
                <ListItemText primary="Teams" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNavigation('/settings')}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'secondary.main',
          boxShadow: 'none'
        }}
      >
        <Toolbar>
          {/* Logo */}
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: isMobile ? 1 : 0,
              fontWeight: 700,
              color: 'secondary.main',
              textDecoration: 'none',
              mr: 4,
              fontFamily: '"Barlow Condensed", sans-serif',
              letterSpacing: '0.04em',
              '&:hover': {
                color: 'secondary.light'
              }
            }}
          >
            {settings.nav_title}
          </Typography>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  variant="outlined"
                  color="secondary"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: isActivePath(item.path) ? 'secondary.main' : 'divider',
                    color: isActivePath(item.path) ? 'secondary.main' : 'text.primary',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'secondary.main',
                      borderColor: 'secondary.main'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
              
              <AdminOnly>
                <Button
                  component={Link}
                  to="/admin"
                  startIcon={<AdminIcon />}
                  variant={isActivePath('/admin') ? 'contained' : 'outlined'}
                  color="error"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    ml: 1
                  }}
                >
                  Admin Panel
                </Button>
              </AdminOnly>
            </Box>
          )}

          {/* Desktop User Actions */}
          {!isMobile && isAuthenticated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {user && (
                <>
                  <Box 
                    onClick={handleUserMenuOpen}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      p: 1,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <Avatar 
                      sx={{ width: 32, height: 32, bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
                      src={resolveMediaUrl(user.avatar)}
                    >
                      {user.username?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" color="text.primary">
                      {user.username}
                    </Typography>
                    {user.role === 'admin' && (
                      <Chip label="Admin" size="small" color="error" />
                    )}
                  </Box>
                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    sx={{
                      mt: 1,
                      '& .MuiPaper-root': {
                        borderRadius: 2,
                        minWidth: 180,
                      }
                    }}
                  >
                    <MenuItem onClick={() => handleUserMenuNavigation('/profile')}>
                      <ListItemIcon>
                        <ProfileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Profile</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleUserMenuNavigation('/teams')}>
                      <ListItemIcon>
                        <TeamIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Teams</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => handleUserMenuNavigation('/settings')}>
                      <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Settings</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
              <Button
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
                variant="outlined"
                color="secondary"
                sx={{ 
                  textTransform: 'none',
                  borderColor: 'divider',
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: 'secondary.main',
                    borderColor: 'secondary.main'
                  }
                }}
              >
                Logout
              </Button>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="secondary"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{
                border: 1,
                borderColor: 'divider',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'secondary.main',
                  borderColor: 'secondary.main'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 250,
            bgcolor: 'background.paper'
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Navbar;